import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, Save, Trash2, AlertTriangle, CheckCircle,
  FileText, ShieldCheck, Search, X, Printer, CreditCard,
} from 'lucide-react';

import { chequeApi } from '@/api/cheque.api';
import type { ChequePrintData } from '@/api/cheque.api';
import { bankAccountApi, bankApi, bankBranchApi } from '@/api/bank.api';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ChequeVoucher } from '@/components/banking/ChequeVoucher';
import { ChequePrintLayer } from '@/components/banking/ChequePrintSheet';
import { ChequePreview }    from '@/components/banking/ChequePreview';
import { exportToExcel } from '@/utils/reportExport';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import { ERPContextBar } from '@/components/erp/ERPContextBar';
import { AuditViewer } from '@/components/erp/AuditViewer';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn } from '@/utils/cn';

import type { RootState } from '@/state/store';
import type { SearchColumn } from '@/components/master/MasterSearchModal';
import type { BankAccountListDto, BankListDto, BankBranchListDto } from '@/types/bank.types';
import type {
  ChequeRecordDto,
  ChequeType,
  ChequePartyType,
  ChequeStatus,
  CreateChequeRequest,
  UpdateChequeRequest,
  ChangeChequeStatusRequest,
} from '@/types/cheque.types';
import { toChequeStatus, chequeStatusValue } from '@/types/cheque.types';
import {
  CHEQUE_TYPE_LABELS,
  CHEQUE_STATUS_LABELS,
  CHEQUE_STATUS_COLORS,
} from '@/types/cheque.types';

// ── Constants ──────────────────────────────────────────────────────────────────

const CHEQUE_TYPES: ChequeType[] = ['Issued', 'Received', 'PostDated'];
const PARTY_TYPES: ChequePartyType[] = ['Customer', 'Supplier', 'Other'];

/**
 * Allowed next statuses per current status.
 *
 * Every entry is a member of the domain enum. This map used to offer `Realized` from a deposited
 * cheque and `Stopped` from an issued one — neither exists in `ChequeStatus.cs`, so both were
 * dead options — while `Cleared`, which is how a deposited cheque normally ends, was not offered
 * at all.
 *
 * It still restates what the server enforces, which is a second source of truth and will drift
 * again. The API already knows the answer and puts it in its refusals ("Allowed: Cleared,
 * Returned"); the durable fix is an endpoint that reports the allowed transitions and a form that
 * asks. Until then this has to match `ChangeChequeStatusCommandHandler`.
 */
const ALLOWED_TRANSITIONS: Partial<Record<ChequeStatus, ChequeStatus[]>> = {
  Draft:     ['Received', 'Issued', 'Cancelled'],
  Received:  ['Deposited', 'Returned', 'Cancelled'],
  Issued:    ['Presented', 'Returned', 'Cancelled'],
  Deposited: ['Cleared', 'Returned'],
  Presented: ['Cleared', 'Returned'],
};

// ── Search columns ─────────────────────────────────────────────────────────────

const BANK_ACCOUNT_COLUMNS: SearchColumn<BankAccountListDto>[] = [
  { key: 'accountNumber', label: 'Account No.', className: 'w-32 font-mono text-teal-700 dark:text-teal-400' },
  { key: 'accountName',   label: 'Account Name' },
  { key: 'currencyCode',  label: 'CCY', className: 'w-14 font-mono text-slate-500 dark:text-gray-400' },
  { key: 'accountType',   label: 'Type', className: 'w-20' },
];

// ── Tabs ───────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'details', label: 'Details',   icon: FileText    },
  { key: 'audit',   label: 'Audit Log', icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Action modal (generic text input) ─────────────────────────────────────────

interface ActionModalProps {
  title:       string;
  placeholder: string;
  required:    boolean;
  onConfirm:   (text: string) => void;
  onCancel:    () => void;
}

const ActionModal = ({ title, placeholder, required, onConfirm, onCancel }: ActionModalProps) => {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">{title}</h3>
        <textarea ref={ref} value={text} onChange={e => setText(e.target.value)}
          placeholder={placeholder} rows={4}
          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none
                     focus:ring-2 focus:ring-teal-500 outline-none" />
        <div className="flex gap-3 mt-4 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600
                       hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
            Cancel
          </button>
          <button onClick={() => onConfirm(text)} disabled={required && !text.trim()}
            className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Change Status Modal ────────────────────────────────────────────────────────

interface ChangeStatusModalProps {
  currentStatus: ChequeStatus;
  onConfirm:     (req: ChangeChequeStatusRequest) => void;
  onCancel:      () => void;
}

const ChangeStatusModal = ({ currentStatus, onConfirm, onCancel }: ChangeStatusModalProps) => {
  const allowed = ALLOWED_TRANSITIONS[currentStatus] ?? [];
  const [newStatus, setNewStatus] = useState<ChequeStatus>(allowed[0] ?? currentStatus);
  const [reason,      setReason]      = useState('');
  const [clearedDate, setClearedDate] = useState('');

  if (allowed.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">Change Status</h3>
          <p className="text-sm text-slate-500 dark:text-gray-400">No further status transitions are available from <strong>{currentStatus}</strong>.</p>
          <div className="flex justify-end mt-4">
            <button onClick={onCancel}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600
                         hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Change Cheque Status</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              New Status <span className="text-red-500">*</span>
            </label>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value as ChequeStatus)}
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 outline-none">
              {allowed.map(s => (
                <option key={s} value={s}>{CHEQUE_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          {newStatus === 'Cleared' && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                Cleared Date <span className="text-red-500">*</span>
              </label>
              <input type="date" value={clearedDate} onChange={e => setClearedDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                           bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                           focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Reason</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Optional reason for status change…" rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none
                         focus:ring-2 focus:ring-teal-500 outline-none" />
          </div>
        </div>

        <div className="flex gap-3 mt-4 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600
                       hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ newStatus: chequeStatusValue(newStatus), reason: reason || null })}
            disabled={newStatus === 'Cleared' && !clearedDate}
            className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
            Change Status
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Field helper ───────────────────────────────────────────────────────────────

const fieldClass = (disabled: boolean) => cn(
  'w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'border-slate-200 dark:border-slate-600',
  disabled
    ? 'bg-slate-50 dark:bg-slate-800 text-slate-500 cursor-default'
    : 'focus:ring-2 focus:ring-teal-500 focus:border-transparent',
);

// ── Main Component ─────────────────────────────────────────────────────────────

export const ChequeFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.BNK_CHEQUE_CREATE);
  const canEdit   = hasPermission(Permissions.BNK_CHEQUE_EDIT);
  const canCancel = hasPermission(Permissions.BNK_CHEQUE_CANCEL);
  const canPrint  = hasPermission(Permissions.BNK_CHEQUE_PRINT);

  const authUser = useSelector((s: RootState) => s.auth.user);
  const session  = useSelector((s: RootState) => s.session.session);

  const today = new Date().toISOString().split('T')[0];

  // ── Form state ────────────────────────────────────────────────────────────
  const [chequeNumber,   setChequeNumber]   = useState('');
  const [chequeDate,     setChequeDate]     = useState(today);
  const [bankAccountId,  setBankAccountId]  = useState('');
  const [bankAccountCode,setBankAccountCode]= useState('');
  const [bankAccountName,setBankAccountName]= useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [chequeType,     setChequeType]     = useState<ChequeType>('Received');
  const [partyType,      setPartyType]      = useState<ChequePartyType>('Customer');
  const [customerId,     setCustomerId]     = useState('');
  const [customerCode,   setCustomerCode]   = useState('');
  const [customerName,   setCustomerName]   = useState('');
  const [supplierId,     setSupplierId]     = useState('');
  const [supplierCode,   setSupplierCode]   = useState('');
  const [supplierName,   setSupplierName]   = useState('');
  const [amount,         setAmount]         = useState<number>(0);
  const [currencyCode,   setCurrencyCode]   = useState(
    authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD',
  );
  const [payee,          setPayee]          = useState('');
  const [payeeName,      setPayeeName]      = useState('');
  const [crossingType,   setCrossingType]   = useState('');
  const [remarks,        setRemarks]        = useState('');
  const [referenceDocument, setReferenceDocument] = useState('');

  // Drawer (customer's) bank + branch — the drawee, distinct from our banking account (GAP-014).
  const [drawerBankId,   setDrawerBankId]   = useState('');
  const [drawerBranchId, setDrawerBranchId] = useState('');
  const [drawerBanks,    setDrawerBanks]    = useState<BankListDto[]>([]);
  const [drawerBranches, setDrawerBranches] = useState<BankBranchListDto[]>([]);

  const [bankOpen, setBankOpen] = useState(false);

  // ── Document state ─────────────────────────────────────────────────────────
  const [cheque,    setCheque]    = useState<ChequeRecordDto | null>(null);
  const [loading,   setLoading]   = useState(!isNew);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [success,   setSuccess]   = useState<string | null>(null);
  const [tab,       setTab]       = useState<Tab>('details');
  const [modal,          setModal]          = useState<'delete' | 'changeStatus' | null>(null);
  const [printOpen,      setPrintOpen]      = useState(false);
  const [chequeTemplate, setChequeTemplate] = useState<ChequePrintData | null>(null);
  // Printing and RECORDING a print are two different events (GAP-008): a render can be previewed,
  // cancelled, or die at the printer, and none of that is a printed cheque. Confirm stays out of
  // reach until something has actually gone to a printer.
  const [sentToPrinter, setSentToPrinter] = useState(false);
  const [confirmingPrint, setConfirmingPrint] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadCheque = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true); setError(null);
    try {
      const res = await chequeApi.getById(id);
      const c = res.data?.data;
      if (!c) throw new Error('Cheque not found');
      setCheque(c);

      setChequeNumber(c.chequeNumber);
      setChequeDate(c.chequeDate.split('T')[0]);
      // A received cheque can have none yet (GAP-013) — the form's own bank picker only applies
      // to one we issue, so viewing one with no bank simply shows the field empty.
      setBankAccountId(c.bankAccountId ?? '');
      setBankAccountCode(c.bankAccountCode ?? '');
      setBankAccountName(c.bankAccountName ?? '');
      setChequeType(c.chequeType);
      setPartyType(c.partyType);
      setCustomerId(c.customerId ?? '');
      setCustomerCode(c.customerCode ?? '');
      setCustomerName(c.customerName ?? '');
      setSupplierId(c.supplierId ?? '');
      setSupplierCode(c.supplierCode ?? '');
      setSupplierName(c.supplierName ?? '');
      setAmount(c.amount);
      setCurrencyCode(c.currencyCode);
      setPayeeName(c.payeeName ?? '');
      setCrossingType(c.crossingType ?? '');
      setRemarks(c.remarks ?? '');
      setReferenceDocument(c.referenceDocument ?? '');
      setDrawerBankId(c.drawerBankId ?? '');
      setDrawerBranchId(c.drawerBranchId ?? '');

      if (c.bankAccountId)
        bankAccountApi.getById(c.bankAccountId)
          .then(r => { if (r.data?.data) setBankAccountNumber(r.data.data.accountNumber); })
          .catch(() => {});
      else
        setBankAccountNumber('');

      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         '',
        locationCode:        null, locationName: null,
        costCenterCode:      null, costCenterName: null,
        financialYearCode:   c.financialYearCode,
        financialPeriodCode: '',
        transactionTypeCode: 'BNK-CHQ',
        documentNumber:      c.chequeNumber,
        currencyCode:        c.currencyCode,
        exchangeRate:        1,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load cheque');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadCheque(); }, [loadCheque]);
  useEffect(() => { return () => { dispatch(clearErpContext()); }; }, [dispatch]);

  // Drawer banks: the master list, loaded once. Branches follow the chosen bank.
  useEffect(() => {
    bankApi.getList({ pageNumber: 1, pageSize: 500 })
      .then(r => setDrawerBanks(r.data.data?.items ?? []))
      .catch(() => setDrawerBanks([]));
  }, []);

  useEffect(() => {
    if (!drawerBankId) { setDrawerBranches([]); return; }
    bankBranchApi.getList({ bankId: drawerBankId, pageNumber: 1, pageSize: 500 })
      .then(r => setDrawerBranches(r.data.data?.items ?? []))
      .catch(() => setDrawerBranches([]));
  }, [drawerBankId]);

  // Normalised: the API sends the enum by value, so comparing the raw field against 'Issued'
  // is always false — which is why the Print Cheque button below never appeared for any cheque.
  const currentStatus = toChequeStatus(cheque?.chequeStatus as ChequeStatus | number | undefined);
  const isDraftEditable = isNew || currentStatus === 'Draft';
  const isEditable = isDraftEditable && (isNew ? canCreate : canEdit);

  const fetchBankAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await bankAccountApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  // ── Print Cheque (layout-based) ───────────────────────────────────────────
  const handlePrintCheque = async () => {
    if (!cheque) return;
    setTemplateLoading(true);
    setError(null);
    try {
      const res = await chequeApi.printData(cheque.id);
      const data = res.data?.data;
      if (!data) throw new Error('No print data returned.');
      // Show it first. Printing straight from the button means the operator learns what the layout
      // does only after a numbered piece of stationery has gone through the machine.
      setSentToPrinter(false);
      setChequeTemplate(data);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Failed to load cheque print layout.',
      );
    } finally {
      setTemplateLoading(false);
    }
  };

  /**
   * Records that the cheque physically printed.
   *
   * The same step the Print Queue performs, called from here so the two entry points cannot differ:
   * before this, printing from the cheque form produced paper and recorded nothing — the cheque sat
   * in the queue as "not printed" for ever and its print history was blank for an instrument that
   * exists.
   *
   * A cheque already printed is refused by the server, and rightly: a second one is a reprint,
   * which needs a reason and belongs on the Print Queue.
   */
  const handleConfirmPrint = async () => {
    if (!cheque) return;
    setConfirmingPrint(true);
    setError(null);
    try {
      await chequeApi.confirmPrint(cheque.id);
      setChequeTemplate(null);
      setSuccess('Cheque recorded as printed.');
      setTimeout(() => setSuccess(null), 4000);
      loadCheque();
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
      setError(res?.data?.errors?.[0]?.message ?? res?.data?.message
        ?? 'Could not record the print. Use the Cheque Print Queue to reprint.');
    } finally {
      setConfirmingPrint(false);
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!bankAccountId) { setError('Please select a bank account.'); return; }
    if (!chequeNumber.trim()) { setError('Cheque number is required.'); return; }
    if (amount <= 0) { setError('Amount must be greater than zero.'); return; }

    setSaving(true);
    try {
      if (isNew) {
        const payload: CreateChequeRequest = {
          companyId:         authUser?.companyId ?? '',
          financialYearCode: session?.financialYearCode ?? '',
          locationId:        null,
          bankAccountId,
          chequeType,
          chequeNumber:      chequeNumber.trim(),
          chequeDate,
          partyType,
          amount,
          currencyCode,
          customerId:        partyType === 'Customer' ? (customerId || null) : null,
          customerCode:      partyType === 'Customer' ? (customerCode || null) : null,
          customerName:      partyType === 'Customer' ? (customerName || null) : null,
          supplierId:        partyType === 'Supplier' ? (supplierId || null) : null,
          supplierCode:      partyType === 'Supplier' ? (supplierCode || null) : null,
          supplierName:      partyType === 'Supplier' ? (supplierName || null) : null,
          referenceDocument: referenceDocument || null,
          remarks:           remarks || null,
          payeeName:         payeeName || null,
          crossingType:      crossingType || null,
          drawerBankId:      chequeType === 'Received' ? (drawerBankId || null) : null,
          drawerBranchId:    chequeType === 'Received' ? (drawerBranchId || null) : null,
        };
        const res = await chequeApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Cheque created successfully.');
        setTimeout(() => navigate(`/banking/cheques/${newId}`), 800);
      } else if (cheque) {
        const payload: UpdateChequeRequest = {
          chequeDate,
          partyType,
          customerId:        partyType === 'Customer' ? (customerId || null) : null,
          customerCode:      partyType === 'Customer' ? (customerCode || null) : null,
          customerName:      partyType === 'Customer' ? (customerName || null) : null,
          supplierId:        partyType === 'Supplier' ? (supplierId || null) : null,
          supplierCode:      partyType === 'Supplier' ? (supplierCode || null) : null,
          supplierName:      partyType === 'Supplier' ? (supplierName || null) : null,
          referenceDocument: referenceDocument || null,
          remarks:           remarks || null,
          payeeName:         payeeName || null,
          crossingType:      crossingType || null,
        };
        await chequeApi.update(cheque.id, payload);
        setSuccess('Cheque updated.');
        setTimeout(() => { setSuccess(null); loadCheque(); }, 800);
      }
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Failed to save cheque.',
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Change Status ─────────────────────────────────────────────────────────
  const handleChangeStatus = async (req: ChangeChequeStatusRequest) => {
    if (!cheque) return;
    setError(null); setSaving(true);
    try {
      await chequeApi.changeStatus(cheque.id, req);
      setSuccess(`Status changed to ${toChequeStatus(req.newStatus)}.`);
      setTimeout(() => { setSuccess(null); loadCheque(); }, 1200);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Failed to change status.',
      );
    } finally { setSaving(false); }
  };

  // ── Delete / Cancel ───────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!cheque) return;
    setSaving(true); setError(null);
    try {
      await chequeApi.delete(cheque.id);
      navigate(AppRoutes.BANK_CHEQUES, { replace: true });
    } catch (e: unknown) {
      setError(
        (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
          ?? (e as { message?: string })?.message ?? 'Delete failed.',
      );
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
      <ERPContextBar />

      {/* Toolbar */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate(AppRoutes.BANK_CHEQUES)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                       hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />

          {isEditable && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              {saving ? <Spinner size="sm" /> : <Save size={14} />} Save
            </button>
          )}

          {!isNew && (ALLOWED_TRANSITIONS[currentStatus]?.length ?? 0) > 0 && (canEdit || canCancel) && (
            <button onClick={() => setModal('changeStatus')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              Change Status
            </button>
          )}

          {!isNew && currentStatus === 'Draft' && canCancel && (
            <button onClick={() => setModal('delete')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Trash2 size={14} /> Delete
            </button>
          )}

          {!isNew && cheque && canPrint && (
            <button onClick={() => setPrintOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <Printer size={14} /> Print Voucher
            </button>
          )}

          {!isNew && cheque && canPrint &&
            (currentStatus === 'Issued' || currentStatus === 'Presented' || currentStatus === 'Cleared') && (
            <button onClick={handlePrintCheque} disabled={templateLoading || saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              {templateLoading ? <Spinner size="sm" /> : <Printer size={14} />} Print Cheque
            </button>
          )}

          {!isNew && (
            <div className="ml-auto flex items-center gap-2">
              <span className={cn('px-3 py-1 rounded-full text-xs font-semibold',
                CHEQUE_STATUS_COLORS[currentStatus] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                {CHEQUE_STATUS_LABELS[currentStatus] ?? currentStatus}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20
                          border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
            <CheckCircle size={14} className="shrink-0" /><span>{success}</span>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard size={18} className="text-teal-600" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {isNew ? 'New Cheque Record' : `Cheque ${cheque?.chequeNumber ?? ''}`}
          </h2>
        </div>
        {!isNew && cheque && (
          <p className="text-xs text-slate-400 mb-0">Created {new Date(cheque.createdDate).toLocaleString()}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
        <div className="flex">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                tab === t.key
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300',
              )}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      <div className="flex-1 overflow-auto">

        {tab === 'details' && (
          <div className="p-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* Cheque Number */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Cheque Number <span className="text-red-500">*</span>
                  </label>
                  <input value={chequeNumber} onChange={e => setChequeNumber(e.target.value)}
                    disabled={!isEditable} placeholder="e.g. 000123"
                    className={fieldClass(!isEditable)} />
                </div>

                {/* Cheque Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Cheque Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={chequeDate} onChange={e => setChequeDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Cheque Type */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Cheque Type <span className="text-red-500">*</span>
                  </label>
                  <select value={chequeType} onChange={e => setChequeType(e.target.value as ChequeType)}
                    disabled={!isNew} className={fieldClass(!isNew)}>
                    {CHEQUE_TYPES.map(t => (
                      <option key={t} value={t}>{CHEQUE_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </div>

                {/* Bank Account */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Bank Account <span className="text-red-500">*</span>
                  </label>
                  <div
                    onClick={() => isEditable && setBankOpen(true)}
                    className={cn(
                      'flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors',
                      'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600',
                      isEditable ? 'cursor-pointer hover:border-teal-400' : 'cursor-default',
                      !bankAccountId && 'text-slate-400',
                    )}
                  >
                    {bankAccountId ? (
                      <>
                        <span className="font-mono text-xs font-semibold text-teal-700 dark:text-teal-400">
                          {bankAccountNumber || bankAccountCode}
                        </span>
                        <span className="text-slate-700 dark:text-slate-300 truncate flex-1 text-xs">{bankAccountName}</span>
                        {isEditable && (
                          <button type="button"
                            onClick={e => { e.stopPropagation(); setBankAccountId(''); setBankAccountNumber(''); setBankAccountName(''); setBankAccountCode(''); }}
                            className="text-slate-300 hover:text-red-500"><X size={12} /></button>
                        )}
                      </>
                    ) : (
                      <>
                        {isEditable && <Search size={12} className="text-slate-300" />}
                        <span className="text-xs italic">{isEditable ? 'Select bank account…' : '—'}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Currency (auto from bank account) */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Currency</label>
                  <input value={currencyCode} onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
                    disabled={!isEditable} maxLength={10}
                    className={cn(fieldClass(!isEditable), 'font-mono uppercase')} />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input type="number" value={amount || ''} onChange={e => setAmount(parseFloat(e.target.value) || 0)}
                    disabled={!isEditable} step="0.01" min="0"
                    className={cn(fieldClass(!isEditable), 'text-right font-mono')} />
                </div>

                {/* Party Type */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Party Type</label>
                  <select value={partyType} onChange={e => { setPartyType(e.target.value as ChequePartyType); setCustomerId(''); setCustomerCode(''); setCustomerName(''); setSupplierId(''); setSupplierCode(''); setSupplierName(''); }}
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    {PARTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Customer lookup (conditional) */}
                {partyType === 'Customer' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Customer Code</label>
                    <input value={customerCode} onChange={e => setCustomerCode(e.target.value)}
                      disabled={!isEditable} placeholder="Code…" className={fieldClass(!isEditable)} />
                  </div>
                )}
                {partyType === 'Customer' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Customer Name</label>
                    <input value={customerName} onChange={e => setCustomerName(e.target.value)}
                      disabled={!isEditable} placeholder="Name…" className={fieldClass(!isEditable)} />
                  </div>
                )}

                {/* Supplier lookup (conditional) */}
                {partyType === 'Supplier' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Supplier Code</label>
                    <input value={supplierCode} onChange={e => setSupplierCode(e.target.value)}
                      disabled={!isEditable} placeholder="Code…" className={fieldClass(!isEditable)} />
                  </div>
                )}
                {partyType === 'Supplier' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Supplier Name</label>
                    <input value={supplierName} onChange={e => setSupplierName(e.target.value)}
                      disabled={!isEditable} placeholder="Name…" className={fieldClass(!isEditable)} />
                  </div>
                )}

                {/* Payee / Payer */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    {chequeType === 'Issued' ? 'Payee' : 'Payer'}
                  </label>
                  <input value={payee} onChange={e => setPayee(e.target.value)}
                    disabled={!isEditable} placeholder="Name…" className={fieldClass(!isEditable)} />
                </div>

                {/* Drawer bank + branch — only for a received cheque (GAP-014). The customer's bank
                    the cheque is drawn on, distinct from our banking account above. */}
                {chequeType === 'Received' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Drawer Bank</label>
                      <select value={drawerBankId}
                        onChange={e => { setDrawerBankId(e.target.value); setDrawerBranchId(''); }}
                        disabled={!isEditable} className={fieldClass(!isEditable)}>
                        <option value="">— None —</option>
                        {drawerBanks.map(b => <option key={b.id} value={b.id}>{b.bankName}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Drawer Branch</label>
                      <select value={drawerBranchId} onChange={e => setDrawerBranchId(e.target.value)}
                        disabled={!isEditable || !drawerBankId} className={fieldClass(!isEditable || !drawerBankId)}>
                        <option value="">{drawerBankId ? '— None —' : 'Pick a bank first'}</option>
                        {drawerBranches.map(br => <option key={br.id} value={br.id}>{br.branchName}</option>)}
                      </select>
                    </div>
                  </>
                )}

                {/* Who it is made out to, and how it is crossed. Both print on the cheque, and
                    both have been on the record since it was written — no screen ever set them. */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Payee</label>
                  <input value={payeeName} onChange={e => setPayeeName(e.target.value)}
                    disabled={!isEditable} placeholder="Who the cheque is made out to"
                    className={fieldClass(!isEditable)} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Crossing</label>
                  <select value={crossingType} onChange={e => setCrossingType(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    {/* The same three the bank account default and the batch print use. */}
                    <option value="">Not stated</option>
                    <option value="ACCOUNT_PAYEE">A/C Payee Only</option>
                    <option value="NOT_NEGOTIABLE">Not Negotiable</option>
                    <option value="OPEN">Open (uncrossed)</option>
                  </select>
                </div>

                {/* Reference Document */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Related Document</label>
                  <input value={referenceDocument} onChange={e => setReferenceDocument(e.target.value)}
                    disabled={!isEditable} placeholder="Optional" className={fieldClass(!isEditable)} />
                </div>

                {/* Remarks */}
                <div className="lg:col-span-3">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Description / Remarks</label>
                  <textarea value={remarks} onChange={e => setRemarks(e.target.value)}
                    disabled={!isEditable} rows={2} placeholder="Notes about this cheque…"
                    className={cn(fieldClass(!isEditable), 'resize-none')} />
                </div>

              </div>
            </div>
          </div>
        )}

        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && cheque ? (
              <AuditViewer entityName="ChequeRecord" entityId={cheque.id} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the cheque first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal === 'delete' && (
        <ActionModal
          title="Delete Draft Cheque"
          placeholder="The draft is cancelled and kept for audit. It can no longer be edited or posted."
          required={false}
          onConfirm={() => { setModal(null); handleDelete(); }}
          onCancel={() => setModal(null)}
        />
      )}

      {modal === 'changeStatus' && cheque && (
        <ChangeStatusModal
          currentStatus={currentStatus}
          onConfirm={req => { setModal(null); handleChangeStatus(req); }}
          onCancel={() => setModal(null)}
        />
      )}

      {/* Bank account picker */}
      <MasterSearchModal<BankAccountListDto>
        isOpen={bankOpen}
        onClose={() => setBankOpen(false)}
        title="Select Bank Account"
        columns={BANK_ACCOUNT_COLUMNS}
        fetchData={fetchBankAccounts}
        onSelect={item => {
          setBankAccountId(item.id);
          setBankAccountNumber(item.accountNumber);
          setBankAccountName(item.accountName);
          setBankAccountCode(item.accountNumber);
          if (item.currencyCode) setCurrencyCode(item.currencyCode);
          setBankOpen(false);
        }}
      />

      {/* Print Voucher */}
      {cheque && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Cheque ${cheque.chequeNumber}`}
          fileBaseName={`Cheque-${cheque.chequeNumber}`}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Cheque',
            title: `Cheque — ${cheque.chequeNumber}`,
            columns: [
              { header: 'Field', key: 'field', width: 28 },
              { header: 'Value', key: 'value', width: 40 },
            ],
            rows: [
              { field: 'Cheque No',        value: cheque.chequeNumber ?? '' },
              { field: 'Cheque Date',      value: cheque.chequeDate?.slice(0, 10) ?? '' },
              { field: 'Cheque Type',      value: cheque.chequeType ?? '' },
              { field: 'Status',           value: cheque.chequeStatus ?? '' },
              { field: 'Bank Account',     value: cheque.bankAccountName ?? '' },
              { field: 'Bank Account Code', value: cheque.bankAccountCode ?? '' },
              { field: 'Party Type',       value: cheque.partyType ?? '' },
              { field: 'Customer',         value: [cheque.customerCode, cheque.customerName].filter(Boolean).join(' — ') },
              { field: 'Supplier',         value: [cheque.supplierCode, cheque.supplierName].filter(Boolean).join(' — ') },
              { field: 'Amount',           value: cheque.amount ?? 0 },
              { field: 'Currency',         value: cheque.currencyCode ?? '' },
              { field: 'Payee',            value: cheque.payeeName ?? '' },
              { field: 'Crossing',         value: cheque.crossingType ?? 'Not crossed' },
              { field: 'Related Document', value: cheque.referenceDocument ?? '' },
              { field: 'Status Changed',   value: cheque.statusChangedDate?.slice(0, 10) ?? '' },
              { field: 'Status Reason',    value: cheque.statusChangeReason ?? '' },
              { field: 'Remarks',          value: cheque.remarks ?? '' },
            ],
          }, `Cheque-${cheque.chequeNumber}`)}
          email={{ documentType: 'Cheque', documentNo: cheque.chequeNumber }}
        >
          <ChequeVoucher
            cheque={cheque}
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}

      {/* Cheque stationery print layer — hidden on screen, printed via window.print() */}
      {chequeTemplate && (
        <>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:hidden"
               onClick={() => setChequeTemplate(null)}>
            <div className="w-full max-w-3xl" onClick={e => e.stopPropagation()}>
              <div className="rounded-lg bg-white p-5 shadow-xl dark:bg-slate-800">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Cheque {chequeTemplate.chequeNumber} — print preview
                  </h3>
                  <button onClick={() => setChequeTemplate(null)} aria-label="Close"
                          className="text-slate-400 hover:text-slate-600">✕</button>
                </div>
                <p className="mb-3 text-sm text-slate-500 dark:text-gray-400">
                  This is the cheque as it will print, at the layout's own measurements. Load the
                  stationery, print, then confirm — confirming is what records that the cheque
                  exists on paper.
                </p>
                <div className="max-h-[55vh] overflow-y-auto rounded bg-slate-100 p-3 dark:bg-slate-900/50">
                  <ChequePreview data={chequeTemplate} />
                </div>
                {sentToPrinter && (
                  <div className="mt-3 rounded border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-300">
                    Sent to the printer. Check the paper before confirming — a cheque printed a
                    second time is a reprint, and that needs a reason.
                  </div>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  <button onClick={() => setChequeTemplate(null)} disabled={confirmingPrint}
                          className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                    {sentToPrinter ? 'Not printed — close' : 'Close'}
                  </button>
                  <button onClick={() => { setSentToPrinter(true); setTimeout(() => window.print(), 150); }}
                          disabled={confirmingPrint}
                          className="rounded border border-orange-600 px-3 py-1.5 text-sm font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50 dark:text-orange-400 dark:hover:bg-orange-900/20">
                    {sentToPrinter ? 'Print again' : 'Print'}
                  </button>
                  <button onClick={handleConfirmPrint} disabled={!sentToPrinter || confirmingPrint}
                          className="rounded bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40">
                    {confirmingPrint ? 'Recording…' : 'Confirm printed'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <ChequePrintLayer cheques={[chequeTemplate]} />
        </>
      )}
    </div>
  );
};
